import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Lock, Plus } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { areaClass, fieldClass } from '../../components/ui/form-classes';
import { removeTeam, upsertTeam } from '../../core/assignment-rules';
import type { ScheduleEntry } from '../../core/conflict-checker';
import { canViewInternalNotes, hasPermission } from '../../core/permissions';
import { toDateKey } from '../../core/task-filters';
import {
  clearTaskDraft,
  isDraftEmpty,
  restorableTaskDraft,
  writeTaskDraft,
} from '../../core/task-draft';
import type { TaskDraftFields } from '../../core/task-draft';
import { getAssignmentSchedule, getTaskTeams, saveTaskTeams } from '../../db/assignment-store';
import { createTask, createTaskType, saveContact, updateTask } from '../../db/task-store';
import { TaskTeamsSection } from './assignment';
import type { TaskTeam } from '../../types/assignment';
import type { Soldier, Vehicle } from '../../types/fleet';
import {
  DEFAULT_TASK_PRIORITY,
  DEFAULT_TASK_STATUS_ID,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_TONES,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  normalizeTaskStatus,
} from '../../types/task';
import type { Task, TaskPriority, TaskStatusId, TaskType } from '../../types/task';
import type { Contact } from '../../types/contact';
import type { User } from '../../types/user';

/** `CREATE_TASK` opens the form, `EDIT_TASK` reopens it on an existing task. */
function PermissionWall({ action, onClose }: { action: string; onClose: () => void }) {
  return (
    <Modal title="אין הרשאה" onClose={onClose}>
      <div className="text-center py-6">
        <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
          <Lock size={22} aria-hidden />
        </div>
        <p className="text-sm text-ink/70">נדרשת הרשאת {action} כדי לבצע פעולה זו.</p>
      </div>
    </Modal>
  );
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-line p-3 space-y-3">
      <h3 className="text-xs font-bold text-ink/50">{title}</h3>
      {children}
    </section>
  );
}

interface AddTaskTypeModalProps {
  user: User;
  onClose: () => void;
  onCreated: (taskType: TaskType) => void;
}

/** The inline "סוג משימה חדש" dialog opened from the creation form. */
export function AddTaskTypeModal({ user, onClose, onCreated }: AddTaskTypeModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!hasPermission(user, 'CREATE_TASK')) {
    return <PermissionWall action="יצירת משימה (CREATE_TASK)" onClose={onClose} />;
  }

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('יש להזין שם לסוג המשימה.');
      return;
    }
    setSaving(true);
    try {
      onCreated(await createTaskType(user, { name, description }));
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(
        /already exists|constraint/i.test(message)
          ? 'סוג משימה בשם זה כבר קיים.'
          : `השמירה נכשלה — ${message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="סוג משימה חדש"
      subtitle="הסוג יתווסף לרשימה ויהיה זמין לכל המשימות הבאות"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : 'הוספה'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Labelled label="שם הסוג">
          <input value={name} onChange={(event) => setName(event.target.value)} className={fieldClass} />
        </Labelled>
        <Labelled label="תיאור (רשות)">
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
            className={areaClass}
          />
        </Labelled>
        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

interface CreateTaskModalProps {
  user: User;
  /** `null` opens the form empty, for a new task. */
  task: Task | null;
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  /** The fleet the vehicle teams are built from. */
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

/**
 * What the form opens with when there is no draft to pour back in: the task
 * being edited, or the defaults a new one starts from. Spelled once, because
 * "discard the draft" means exactly "go back to these".
 */
function baselineFields(task: Task | null, taskTypes: readonly TaskType[]): TaskDraftFields {
  return {
    title: task?.title ?? '',
    taskTypeId: task?.taskTypeId ?? taskTypes[0]?.id ?? '',
    description: task?.description ?? '',
    date: task?.date ?? toDateKey(),
    startTime: task?.startTime ?? '08:00',
    estEndTime: task?.estEndTime ?? '12:00',
    priority: task?.priority ?? DEFAULT_TASK_PRIORITY,
    statusId: task?.statusId ?? DEFAULT_TASK_STATUS_ID,
    contactMode: task?.manualContact ? 'manual' : 'saved',
    contactId: task?.contactId ?? '',
    manualName: task?.manualContact?.name ?? '',
    manualPhone: task?.manualContact?.phone ?? '',
    manualRole: task?.manualContact?.role ?? '',
    destinationAddress: task?.destinationAddress ?? '',
    destinationLink: task?.destinationLink ?? '',
    operationalNotes: task?.operationalNotes ?? '',
    internalNotes: task?.internalNotes ?? '',
  };
}

/** A draft is plain text on disk — an unknown value never reaches the form. */
const asPriority = (value: string): TaskPriority =>
  TASK_PRIORITIES.includes(value as TaskPriority) ? (value as TaskPriority) : DEFAULT_TASK_PRIORITY;

const asStatus = (value: string): TaskStatusId => normalizeTaskStatus(value);

/**
 * Create / edit a task. `CREATE_TASK` (or `EDIT_TASK` on an existing record)
 * is checked here so the board never offers an action it cannot complete, and
 * again inside the store — the store guard is the one that protects the data.
 *
 * Internal manager notes are only rendered, and only ever written, for a
 * viewer the permissions engine allows to read them.
 *
 * Nothing typed here is lost to a reload. Every field is mirrored into
 * `localStorage` as it is typed (`src/core/task-draft.ts`) and poured back in
 * when the form re-mounts on the same target — the answer to a phone that
 * treats a downward swipe as "refresh". A save or a cancel drops the draft; it
 * is the *unsent* form, and an abandoned one expires within the day.
 */
export function CreateTaskModal({
  user,
  task,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  onClose,
  onSaved,
}: CreateTaskModalProps) {
  const canSubmit = hasPermission(user, task ? 'EDIT_TASK' : 'CREATE_TASK');
  const canAddType = hasPermission(user, 'CREATE_TASK');
  const canAssign = hasPermission(user, 'ASSIGN_TASK');
  const canSeeInternal = canViewInternalNotes(user);

  /** Locally grown catalogue, so a type added inline appears at once. */
  const [types, setTypes] = useState<TaskType[]>([...taskTypes]);
  const [typeModalOpen, setTypeModalOpen] = useState(false);

  /** The target the draft is filed under: `''` for a new task, else its id. */
  const draftTaskId = task?.id ?? '';
  /** Read once, on mount — the form is what owns the values from then on. */
  const [restoredDraft] = useState(() => restorableTaskDraft(draftTaskId));
  const [initial] = useState<TaskDraftFields>(() => {
    const baseline = baselineFields(task, taskTypes);
    return restoredDraft ? { ...baseline, ...restoredDraft } : baseline;
  });
  /** Whether the operator is looking at restored work — said, not assumed. */
  const [draftNotice, setDraftNotice] = useState(restoredDraft !== null);

  const [title, setTitle] = useState(initial.title);
  const [taskTypeId, setTaskTypeId] = useState(initial.taskTypeId);
  const [description, setDescription] = useState(initial.description);
  const [date, setDate] = useState(initial.date);
  const [startTime, setStartTime] = useState(initial.startTime);
  const [estEndTime, setEstEndTime] = useState(initial.estEndTime);
  const [priority, setPriority] = useState<TaskPriority>(asPriority(initial.priority));
  const [statusId, setStatusId] = useState<TaskStatusId>(asStatus(initial.statusId));

  const [contactMode, setContactMode] = useState<'saved' | 'manual'>(initial.contactMode);
  const [contactId, setContactId] = useState(initial.contactId);
  const [manualName, setManualName] = useState(initial.manualName);
  const [manualPhone, setManualPhone] = useState(initial.manualPhone);
  const [manualRole, setManualRole] = useState(initial.manualRole);
  const [saveForFuture, setSaveForFuture] = useState(false);

  const [destinationAddress, setDestinationAddress] = useState(initial.destinationAddress);
  const [destinationLink, setDestinationLink] = useState(initial.destinationLink);
  const [operationalNotes, setOperationalNotes] = useState(initial.operationalNotes);
  const [internalNotes, setInternalNotes] = useState(initial.internalNotes);

  /**
   * The convoy. On an existing task it is read from the store; on a new one it
   * is held here until the task has an id to bind the teams to.
   */
  const [teams, setTeams] = useState<TaskTeam[]>([]);

  /** What every other open task has already booked, for the overlap check. */
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!task) return;
    let alive = true;
    void getTaskTeams(task.id).then((loaded) => {
      if (alive) setTeams(loaded);
    });
    return () => {
      alive = false;
    };
  }, [task]);

  useEffect(() => {
    let alive = true;
    void getAssignmentSchedule(task?.id).then((booked) => {
      if (alive) setSchedule(booked);
    });
    return () => {
      alive = false;
    };
  }, [task]);

  /**
   * The window the crew is being checked against — read from the form as it is
   * typed, so moving the task an hour re-runs every conflict at once.
   */
  const taskWindow = useMemo(
    () => ({
      id: task?.id ?? '',
      title: title.trim() || 'המשימה הנוכחית',
      date,
      startTime,
      estEndTime,
      statusId,
    }),
    [task?.id, title, date, startTime, estEndTime, statusId],
  );

  /** The form as the draft records it — everything except the convoy. */
  const draftFields = useMemo<TaskDraftFields>(
    () => ({
      title,
      taskTypeId,
      description,
      date,
      startTime,
      estEndTime,
      priority,
      statusId,
      contactMode,
      contactId,
      manualName,
      manualPhone,
      manualRole,
      destinationAddress,
      destinationLink,
      operationalNotes,
      internalNotes,
    }),
    [
      title,
      taskTypeId,
      description,
      date,
      startTime,
      estEndTime,
      priority,
      statusId,
      contactMode,
      contactId,
      manualName,
      manualPhone,
      manualRole,
      destinationAddress,
      destinationLink,
      operationalNotes,
      internalNotes,
    ],
  );

  // Mirrored as it is typed. A form nobody has written anything into holds no
  // draft at all — otherwise merely opening the dialog would leave one behind.
  useEffect(() => {
    if (isDraftEmpty(draftFields)) {
      clearTaskDraft();
      return;
    }
    writeTaskDraft({ ...draftFields, taskId: draftTaskId });
  }, [draftFields, draftTaskId]);

  /** Throws the restored work away and returns the form to where it opened. */
  const discardDraft = () => {
    const baseline = baselineFields(task, taskTypes);
    setTitle(baseline.title);
    setTaskTypeId(baseline.taskTypeId);
    setDescription(baseline.description);
    setDate(baseline.date);
    setStartTime(baseline.startTime);
    setEstEndTime(baseline.estEndTime);
    setPriority(asPriority(baseline.priority));
    setStatusId(asStatus(baseline.statusId));
    setContactMode(baseline.contactMode);
    setContactId(baseline.contactId);
    setManualName(baseline.manualName);
    setManualPhone(baseline.manualPhone);
    setManualRole(baseline.manualRole);
    setDestinationAddress(baseline.destinationAddress);
    setDestinationLink(baseline.destinationLink);
    setOperationalNotes(baseline.operationalNotes);
    setInternalNotes(baseline.internalNotes);
    clearTaskDraft();
    setDraftNotice(false);
  };

  /** Closing without saving is a decision: the draft goes with the dialog. */
  const handleClose = () => {
    clearTaskDraft();
    onClose();
  };

  if (!canSubmit) {
    return (
      <PermissionWall
        action={task ? 'עריכת משימה (EDIT_TASK)' : 'יצירת משימה (CREATE_TASK)'}
        onClose={onClose}
      />
    );
  }

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim()) {
      setError('כותרת המשימה היא שדה חובה.');
      return;
    }
    if (!taskTypeId) {
      setError('יש לבחור סוג משימה.');
      return;
    }
    if (!date) {
      setError('יש לבחור תאריך למשימה.');
      return;
    }
    if (!startTime) {
      setError('יש להזין שעת התחלה.');
      return;
    }
    if (contactMode === 'manual' && saveForFuture && (!manualName.trim() || !manualPhone.trim())) {
      setError('לשמירת איש קשר נדרשים שם וטלפון.');
      return;
    }

    setSaving(true);
    try {
      let resolvedContactId = contactMode === 'saved' ? contactId : '';
      let manualContact =
        contactMode === 'manual'
          ? { name: manualName, phone: manualPhone, role: manualRole }
          : undefined;

      // Filing the contact turns a one-off into a saved one, and the task
      // points at the record from then on.
      if (contactMode === 'manual' && saveForFuture) {
        const saved = await saveContact(user, {
          fullName: manualName,
          role: manualRole,
          organization: '',
          primaryPhone: manualPhone,
        });
        resolvedContactId = saved.id;
        manualContact = undefined;
      }

      const payload = {
        title,
        taskTypeId,
        description,
        date,
        startTime,
        estEndTime,
        priority,
        statusId,
        contactId: resolvedContactId,
        manualContact,
        destinationAddress,
        destinationLink,
        operationalNotes,
        ...(canSeeInternal ? { internalNotes } : {}),
      };

      const saved = task ? await updateTask(user, task.id, payload) : await createTask(user, payload);

      // The crew is bound to the task only once the task itself has an id.
      if (saved && canAssign) await saveTaskTeams(user, saved.id, teams);

      // Filed — the draft has served its purpose and must not outlive it.
      clearTaskDraft();
      await onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`השמירה נכשלה — ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        title={task ? 'עריכת משימה' : 'משימה חדשה'}
        subtitle={task ? task.title : 'פתיחת משימת שינוע חדשה ליומן הפלוגה'}
        onClose={handleClose}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={handleClose}
              className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
            >
              ביטול
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={saving}
              className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'שומר…' : task ? 'שמירת שינויים' : 'פתיחת משימה'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {draftNotice ? (
            <div
              role="status"
              className="flex items-start justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <span className="min-w-0">
                שוחזרה טיוטה שנשמרה — הפרטים שהוזנו לפני רענון הדף הוחזרו לטופס.
              </span>
              <button
                type="button"
                onClick={discardDraft}
                className="shrink-0 h-8 px-3 rounded-lg border border-amber-400 text-xs font-medium hover:bg-amber-100"
              >
                נקה טיוטה
              </button>
            </div>
          ) : null}

          <Labelled label="כותרת המשימה">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="לדוגמה: שינוע דרילר לאתר הצפוני"
              className={fieldClass}
            />
          </Labelled>

          <div>
            <span className="block text-xs text-ink/60 mb-1">סוג משימה</span>
            <div className="flex gap-2">
              <select
                value={taskTypeId}
                onChange={(event) => setTaskTypeId(event.target.value)}
                aria-label="סוג משימה"
                className={fieldClass}
              >
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {canAddType ? (
                <button
                  type="button"
                  onClick={() => setTypeModalOpen(true)}
                  title="הוספת סוג משימה"
                  className="shrink-0 h-10 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-1.5"
                >
                  <Plus size={16} aria-hidden />
                  סוג חדש
                </button>
              ) : null}
            </div>
          </div>

          <Labelled label="תיאור המשימה">
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              className={areaClass}
            />
          </Labelled>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Labelled label="תאריך">
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                data-dir="ltr"
                className={fieldClass}
              />
            </Labelled>
            <Labelled label="שעת התחלה">
              <input
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                data-dir="ltr"
                className={fieldClass}
              />
            </Labelled>
            <Labelled label="שעת סיום משוערת">
              <input
                type="time"
                value={estEndTime}
                onChange={(event) => setEstEndTime(event.target.value)}
                data-dir="ltr"
                className={fieldClass}
              />
            </Labelled>
          </div>

          <fieldset>
            <legend className="text-xs text-ink/60 mb-1">דחיפות</legend>
            <div className="flex flex-wrap gap-1.5">
              {TASK_PRIORITIES.map((option) => {
                const selected = priority === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setPriority(option)}
                    aria-pressed={selected}
                    className={[
                      'text-xs px-3 py-1.5 rounded-lg border transition-colors',
                      selected
                        ? `${TASK_PRIORITY_TONES[option]} font-bold`
                        : 'border-line bg-canvas text-ink/60',
                    ].join(' ')}
                  >
                    {TASK_PRIORITY_LABELS[option]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {task ? (
            <Labelled label="סטטוס">
              <select
                value={statusId}
                onChange={(event) => setStatusId(event.target.value as TaskStatusId)}
                className={fieldClass}
              >
                {TASK_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {TASK_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </Labelled>
          ) : null}

          <Section title="איש קשר למשימה">
            <div className="flex gap-1.5">
              {(['saved', 'manual'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setContactMode(mode)}
                  aria-pressed={contactMode === mode}
                  className={[
                    'text-xs px-3 py-1.5 rounded-lg border transition-colors',
                    contactMode === mode
                      ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
                      : 'border-line bg-canvas text-ink/60',
                  ].join(' ')}
                >
                  {mode === 'saved' ? 'בחירה מאנשי הקשר' : 'הזנה ידנית'}
                </button>
              ))}
            </div>

            {contactMode === 'saved' ? (
              <Labelled label="איש קשר שמור">
                <select
                  value={contactId}
                  onChange={(event) => setContactId(event.target.value)}
                  className={fieldClass}
                >
                  <option value="">ללא איש קשר</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.fullName}
                      {contact.role ? ` — ${contact.role}` : ''}
                    </option>
                  ))}
                </select>
              </Labelled>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Labelled label="שם">
                    <input
                      value={manualName}
                      onChange={(event) => setManualName(event.target.value)}
                      className={fieldClass}
                    />
                  </Labelled>
                  <Labelled label="טלפון">
                    <input
                      type="tel"
                      value={manualPhone}
                      onChange={(event) => setManualPhone(event.target.value)}
                      inputMode="tel"
                      className={fieldClass}
                    />
                  </Labelled>
                </div>
                <Labelled label="תפקיד (רשות)">
                  <input
                    value={manualRole}
                    onChange={(event) => setManualRole(event.target.value)}
                    className={fieldClass}
                  />
                </Labelled>
                <label className="flex items-center gap-2 text-sm text-ink/70">
                  <input
                    type="checkbox"
                    checked={saveForFuture}
                    onChange={(event) => setSaveForFuture(event.target.checked)}
                    className="w-4 h-4 accent-brand-500"
                  />
                  שמור איש קשר זה לשימוש עתידי
                </label>
              </div>
            )}
          </Section>

          <Section title="יעד">
            <Labelled label="כתובת היעד">
              <input
                value={destinationAddress}
                onChange={(event) => setDestinationAddress(event.target.value)}
                placeholder="לדוגמה: מוצב מערב — שער הכלים"
                className={fieldClass}
              />
            </Labelled>
            <Labelled label="קישור ניווט (Waze / Google Maps)">
              <input
                type="url"
                value={destinationLink}
                onChange={(event) => setDestinationLink(event.target.value)}
                placeholder="https://…"
                data-dir="ltr"
                className={fieldClass}
              />
            </Labelled>
          </Section>

          <Section title="צוותים ורכבים — כל צוות משויך לרכב אחד">
            <TaskTeamsSection
              taskId={task?.id ?? ''}
              teams={teams}
              vehicles={vehicles}
              soldiers={soldiers}
              task={taskWindow}
              schedule={schedule}
              managerId={user.id}
              user={user}
              canManage={canAssign}
              onSaveTeam={(team) => setTeams(upsertTeam(teams, team))}
              onRemoveTeam={(team) => setTeams(removeTeam(teams, team.id))}
            />
            {!canAssign ? (
              <p className="text-xs text-ink/40 flex items-center gap-1.5">
                <Lock size={13} aria-hidden />
                שיבוץ רכבים וצוותים דורש הרשאת שיבוץ משימה (ASSIGN_TASK).
              </p>
            ) : null}
          </Section>

          <Section title="הערות">
            <Labelled label="הערות מבצעיות — גלויות לכל מי שמשובץ למשימה">
              <textarea
                value={operationalNotes}
                onChange={(event) => setOperationalNotes(event.target.value)}
                rows={2}
                className={areaClass}
              />
            </Labelled>

            {canSeeInternal ? (
              <Labelled label="הערות פנימיות למנהלים בלבד">
                <textarea
                  value={internalNotes}
                  onChange={(event) => setInternalNotes(event.target.value)}
                  rows={2}
                  className={`${areaClass} border-amber-200 bg-amber-50/40`}
                />
              </Labelled>
            ) : (
              <p className="text-xs text-ink/40 flex items-center gap-1.5">
                <Lock size={13} aria-hidden />
                הערות פנימיות גלויות למנהלים בלבד (VIEW_ALL_TASKS).
              </p>
            )}
          </Section>

          {error ? (
            <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
              {error}
            </p>
          ) : null}
        </div>
      </Modal>

      {typeModalOpen ? (
        <AddTaskTypeModal
          user={user}
          onClose={() => setTypeModalOpen(false)}
          onCreated={(created) => {
            setTypes((current) => [...current, created]);
            setTaskTypeId(created.id);
          }}
        />
      ) : null}
    </>
  );
}
